import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { color } from '../../../app/theme';
import IconEdit from '../../../assets/images/Icons/IconEdit';
import { setState } from '../../../features/mainSlice';
import WhatsAppListCardDialog from '../WhatsAppListCardDialog';
import Icons from './Icons';
import Template from './Template';
import TemplateDropdown from './TemplateDropdown';

const useStyles = makeStyles(
  () => ({
    root: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: 12,
    },
    dropdown: {
      flex: '0 0 auto',
    },
    contentContainer: {
      position: 'relative',
      width: '100%',
    },
    content: {
      cursor: 'pointer',
      width: '100%',
      height: 44,
      fontSize: 15,
      color: color.$3,
      lineHeight: 1,
      borderRadius: 24,
      border: 'solid 1px #bdbdbd',
      backgroundColor: '#ffffff',
      padding: '15px 48px 14px 24px',
      resize: 'none',
      outline: 'none',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      '&::placeholder': {
        fontSize: 15,
        color: color.$3,
      },
    },
    error: { border: `solid 1px ${color.$17}` },
    icon: {
      cursor: 'pointer',
      position: 'absolute',
      top: 10,
      right: 16,
    },
  }),
  {
    name: 'WaListTemplate',
  },
);

function WaListTemplate({
  template,
  onChange,
  onChangeType,
  onDelete,
  platformType,
  onUp,
  onDown,
  messageLength,
  messageIndex,
  disableTemplate,
  isError,
}) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const isShowDialog = useSelector(store => store.ma.isShowWaListDialog);
  const isShowCardDialogTemplateIndex = useSelector(
    store => store.ma.isShowCardDialogTemplateIndex,
  );
  const { t } = useTranslation();

  function handleOpenOptionalCardDialog() {
    dispatch(
      setState({
        isShowWaListDialog: true,
        isShowCardDialogTemplateIndex: messageIndex,
      }),
    );
  }

  function handleOnSubmit(d) {
    dispatch(
      setState({
        isShowWaListDialog: false,
      }),
    );
    onChange(d);
  }

  function handleOnCancel() {
    dispatch(
      setState({
        isShowWaListDialog: false,
      }),
    );
  }

  return (
    <Template>
      <div className={classes.root}>
        <TemplateDropdown
          className={classes.dropdown}
          platformType={platformType}
          messageTemplatetype="waList"
          onChange={onChangeType}
        />
        <Icons
          index={template.index}
          onUp={onUp}
          onDown={onDown}
          onDelete={onDelete}
          messageLength={messageLength}
        />
      </div>
      <div
        className={classes.contentContainer}
        onClick={handleOpenOptionalCardDialog}
      >
        <input
          className={classnames(classes.content, { [classes.error]: isError })}
          value={template.name ?? ''}
          placeholder={t('templates.edit-waList')}
          readOnly
        />
        <div className={classes.icon}>
          <IconEdit
            className={classes.iconSvg}
            id={`edit-${uuidv4()}`}
            color={color.$10}
            hoverColor={color.$10}
          />
        </div>
      </div>
      {isShowDialog && isShowCardDialogTemplateIndex === messageIndex && (
        <WhatsAppListCardDialog
          name={template.name}
          templateNote={template?.templateNote || ''}
          templateExpiredAt={template?.templateExpiredAt}
          templateId={template.templateId}
          data={template.data.elements[0]}
          onSubmit={handleOnSubmit}
          onCancel={handleOnCancel}
          messageIndex={template.index}
          disableTemplate={disableTemplate}
        />
      )}
    </Template>
  );
}

WaListTemplate.defaultProps = {
  onChange: () => {},
  onChangeType: () => {},
  onDelete: () => {},
  onUp: () => {},
  onDown: () => {},
  messageLength: 0,
};

WaListTemplate.propTypes = {
  template: PropTypes.shape({
    templateType: PropTypes.oneOf(['card']),
    data: PropTypes.shape({
      content: PropTypes.string,
    }),
  }).isRequired,
  onChange: PropTypes.func,
  onChangeType: PropTypes.func,
  onDelete: PropTypes.func,
  onUp: PropTypes.func,
  onDown: PropTypes.func,
  messageLength: PropTypes.number,
};

export default WaListTemplate;
